import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, X, Clock } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import {
  GraphMember,
  useFamilyGraph,
} from '@/contexts/FamilyGraphContext';
import type {
  CanonicalViewerMemberProjection,
} from '@/governor/tree/CanonicalTreeViewerProjection';
import MemberAvatar from './MemberAvatar';

interface Props {
  members: GraphMember[];
  canonicalViewerProjections:
    ReadonlyMap<
      string,
      CanonicalViewerMemberProjection
    >;
}

const sideLabel = (s: string) =>
  s === 'mother' ? "Mother's Side" : s === 'father' ? "Father's Side" : s === 'self' ? 'You' : 'Both';

const PendingApprovalsPanel: React.FC<Props> = ({
  members,
  canonicalViewerProjections,
}) => {
  const {
    approveMember,
    rejectMember,
  } = useFamilyGraph();
  const pending = members.filter((m) => m.approvalStatus === 'pending');

  const labelFor = (m: GraphMember): string =>
    canonicalViewerProjections
      .get(m.id)
      ?.kinshipSemantics.primary.preciseLabel
      ?.trim() ||
    'Relationship unresolved';

  if (pending.length === 0) {
    return (
      <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-center">
        <Check className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
        <h4 className="font-semibold text-emerald-900">All caught up!</h4>
        <p className="text-sm text-emerald-700">No pending family additions to review.</p>
      </div>
    );
  }

  const handleApprove = async (id: string, name: string) => {
    try {
      await approveMember(id);
      toast.success(`${name} approved`);
    } catch (err: any) {
      toast.error(err?.message || 'Approve failed');
    }
  };

  const handleReject = async (id: string, name: string) => {
    try {
      await rejectMember(id);
      toast.success(`${name} rejected`);
    } catch (err: any) {
      toast.error(err?.message || 'Reject failed');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-amber-700 font-semibold">
        <Clock className="w-4 h-4" />
        {pending.length} pending {pending.length === 1 ? 'addition' : 'additions'}
      </div>
      {pending.map((m) => (
        <div
          key={m.id}
          className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200"
        >
          <MemberAvatar name={m.name} photoUrl={m.photoUrl} size="sm" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 truncate">{m.name}</div>
            <div className="text-xs text-slate-600">
              {labelFor(m)} · {sideLabel(m.side)}
              {m.birthYear ? ` · b. ${m.birthYear}` : ''}
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              onClick={() => handleApprove(m.id, m.name)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3"
            >
              <Check className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleReject(m.id, m.name)}
              className="border-rose-300 text-rose-700 hover:bg-rose-50 h-8 px-3"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingApprovalsPanel;
